import { useServiceFactory } from '@euler/services/factory';
import { useCallback, useEffect, useState } from 'react';
import {
  PracticeScene,
  sceneFromPractice,
  sceneFromReplay,
} from './practiceScene';

export type PracticeOpening =
  | { kind: 'practice'; attemptId: string }
  | { kind: 'replay'; attemptId: string; momentTurn: number };

export type OpenSceneState =
  | { kind: 'loading' }
  | { kind: 'failed' }
  | { kind: 'open'; scene: PracticeScene };

/**
 * Opens (or resumes) a practice, or opens a replay of one moment, once
 * `ready` (the mute preference is known).
 */
export function useOpenPracticeScene(
  opening: PracticeOpening,
  speak: boolean,
  ready: boolean,
) {
  const { coachingService } = useServiceFactory();
  const [state, setState] = useState<OpenSceneState>({ kind: 'loading' });
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => setReloadKey(k => k + 1), []);
  const key =
    opening.kind === 'practice'
      ? opening.attemptId
      : `${opening.attemptId}:${opening.momentTurn}`;
  useEffect(() => {
    if (!ready) return;
    let current = true;
    setState({ kind: 'loading' });
    const open =
      opening.kind === 'practice'
        ? coachingService
            .openPractice(opening.attemptId, speak)
            .then(sceneFromPractice)
        : coachingService
            .openReplay(opening.attemptId, opening.momentTurn, speak)
            .then(sceneFromReplay);
    open
      .then(scene => current && setState({ kind: 'open', scene }))
      .catch(error => {
        console.error('coaching practice open failed', error);
        if (current) setState({ kind: 'failed' });
      });
    return () => {
      current = false;
    };
    // `speak` is read once: a later mute does not reopen the scene.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coachingService, key, ready, reloadKey]);
  return { state, reload };
}
